import {
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { useState } from "react";
import { API_URL } from "../api/auth";
import { useClerkToken } from "../hooks/use-clerk-token";
import { useSafeNavigateBack } from "../hooks/use-safe-navigate-back";

export function IntegrationsSlackSetupPage() {
  const navigateBack = useSafeNavigateBack("/configuration?tab=integrations");
  const { token } = useClerkToken();
  const { enqueueSnackbar } = useSnackbar();

  const [channel, setChannel] = useState("");
  const [createNotificationConfig, setCreateNotificationConfig] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!channel.trim()) {
      enqueueSnackbar("Please enter a Slack channel", { variant: "error" });
      return;
    }

    if (!token) {
      enqueueSnackbar("Please log in to continue", { variant: "error" });
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(
        `${API_URL}/oauth/slack/setup-notifications?default_channel=${encodeURIComponent(
          channel,
        )}&create_notification_config=${createNotificationConfig}`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Failed to setup notifications");
      }

      enqueueSnackbar("Slack notifications configured successfully!", {
        variant: "success",
      });

      // Redirect to previous page after success
      setTimeout(() => {
        navigateBack();
      }, 1500);
    } catch (error) {
      console.error("Failed to setup Slack notifications:", error);
      enqueueSnackbar(
        error instanceof Error ? error.message : "Failed to setup Slack notifications",
        { variant: "error" },
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = () => {
    navigateBack();
  };

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        p: 2,
        backgroundColor: "background.default",
      }}
    >
      <Card sx={{ maxWidth: 600, width: "100%" }}>
        <CardContent>
          <Stack spacing={3}>
            <Box>
              <Typography variant="h5" gutterBottom>
                Setup Slack Notifications
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Slack connected successfully! Now configure where you'd like to receive test
                notifications.
              </Typography>
            </Box>

            <TextField
              label="Default Channel"
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              placeholder="#qa-alerts"
              helperText='Enter a channel name (e.g., "#general") or channel ID'
              fullWidth
              required
              disabled={isSubmitting}
            />

            <FormControlLabel
              control={
                <Checkbox
                  checked={createNotificationConfig}
                  onChange={(e) => setCreateNotificationConfig(e.target.checked)}
                  disabled={isSubmitting}
                />
              }
              label="Create first notification configuration with this channel"
            />

            <Typography variant="body2" color="text.secondary">
              You can add more notification channels later in the Notifications settings.
            </Typography>

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={handleSkip} disabled={isSubmitting}>
                Skip for now
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={isSubmitting || !channel.trim()}
              >
                {isSubmitting ? <CircularProgress size={24} color="inherit" /> : "Complete Setup"}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
